// Auto-reconnecting WebSocket client that multiplexes notifications + chat
// over a single connection. Authentication: the API exposes
// POST /v5/realtime/ticket which returns a 60s HMAC ticket bound to the
// current user (CSRF-protected, cookie-auth). We open
// wss://<api>/v5/realtime?ticket=... and the server upgrades.
//
// Public surface: `subscribe(type, handler)` returns an unsubscribe fn.
// `ensureOpen()` is idempotent — call from any component that needs realtime.
// `close()` exists for tests and on-logout cleanup.
//
// Reconnect policy: exponential backoff up to 30s, jittered. Reconnect
// drops the current ticket and re-fetches before opening.

import { apiBaseUrl } from '../lib/env';

type Handler = (payload: unknown) => void;

// idle      : not connected, will (re)connect when something subscribes
// connecting: socket is being negotiated
// open      : socket is connected and pumping
// closed    : explicit close() was called; don't reconnect until subscribe()
// paused    : ticket fetch came back 401; the user looks logged out. Stop
//             retrying until resume() is called (the SPA calls it on
//             successful login).
type SocketState = 'idle' | 'connecting' | 'open' | 'closed' | 'paused';

interface RealtimeMessage {
  type: string;
  payload: unknown;
}

class RealtimeClient {
  private socket: WebSocket | null = null;
  private state: SocketState = 'idle';
  private subs = new Map<string, Set<Handler>>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private explicitlyClosed = false;

  // Fired when the socket transitions from not-open to open. Used by
  // chat / notification hooks to catch up: any messages the server
  // pushed while the socket was closed need to be refetched from REST.
  private openHandlers = new Set<() => void>();

  onOpen(handler: () => void): () => void {
    this.openHandlers.add(handler);
    return () => this.openHandlers.delete(handler);
  }

  // Reactive connected-state for React (useSyncExternalStore). Fires whenever
  // the socket transitions open <-> not-open, so REST polls can be gated off
  // while the WS is live (the WS push keeps caches fresh) and resume when it
  // drops. `connected` mirrors state === 'open'.
  private connectedHandlers = new Set<() => void>();

  getConnected = (): boolean => this.state === 'open';

  onConnectedChange = (handler: () => void): (() => void) => {
    this.connectedHandlers.add(handler);
    return () => this.connectedHandlers.delete(handler);
  };

  private notifyConnected(): void {
    for (const h of this.connectedHandlers) {
      try { h(); } catch (err) { console.warn('[realtime] connected handler threw', err); }
    }
  }

  subscribe(type: string, handler: Handler): () => void {
    let set = this.subs.get(type);
    if (!set) {
      set = new Set();
      this.subs.set(type, set);
    }
    set.add(handler);
    void this.ensureOpen();
    return () => {
      const s = this.subs.get(type);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) this.subs.delete(type);
    };
  }

  /**
   * Send a control frame to the server. Best-effort: if the socket isn't
   * open we silently drop it (the only sender today is the activity
   * heartbeat, which is naturally re-sent and backfilled by the snapshot
   * poll, so a dropped frame is harmless). Returns whether it was sent.
   */
  send(type: string, payload?: unknown): boolean {
    if (this.state !== 'open' || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      this.socket.send(JSON.stringify({ type, payload: payload ?? null }));
      return true;
    } catch (err) {
      console.warn('[realtime] send failed', err);
      return false;
    }
  }

  async ensureOpen(): Promise<void> {
    if (this.state === 'open' || this.state === 'connecting' || this.state === 'paused') return;
    this.explicitlyClosed = false;
    await this.connect();
  }

  /**
   * Wake the client up after auth state changes (e.g. login success).
   * If we're paused due to a previous 401, drop the paused flag and try
   * again. No-op if already connected.
   */
  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'idle';
    this.reconnectAttempts = 0;
    void this.ensureOpen();
  }

  close(): void {
    this.explicitlyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try { this.socket.close(1000, 'client closing'); } catch { /* ignore */ }
    }
    this.socket = null;
    this.state = 'closed';
  }

  private async connect(): Promise<void> {
    this.state = 'connecting';

    let ticket: string;
    try {
      const csrf = readCookie('v5_csrf');
      const res = await fetch(`${apiBaseUrl}/v5/realtime/ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      });
      if (res.status === 401) {
        // The user looks logged out (expired cookie / cleared session).
        // Park the client until resume() is called, so we don't burn the
        // network busy-retrying a request that needs a fresh login first.
        console.info('[realtime] ticket 401 — pausing until session resumes');
        this.state = 'paused';
        return;
      }
      if (!res.ok) throw new Error(`ticket fetch ${res.status}`);
      const body = (await res.json()) as { ticket: string };
      ticket = body.ticket;
    } catch (err) {
      console.warn('[realtime] ticket fetch failed; will retry', err);
      this.state = 'idle';
      this.scheduleReconnect();
      return;
    }

    // Build the wss URL by upgrading the http(s) base.
    const wsBase = apiBaseUrl.replace(/^http/, 'ws');
    const url = `${wsBase}/v5/realtime?ticket=${encodeURIComponent(ticket)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.warn('[realtime] WebSocket ctor failed', err);
      this.state = 'idle';
      this.scheduleReconnect();
      return;
    }

    this.socket = ws;

    ws.onopen = () => {
      this.state = 'open';
      this.reconnectAttempts = 0;
      this.notifyConnected();
      for (const h of this.openHandlers) {
        try { h(); } catch (err) { console.warn('[realtime] onOpen handler threw', err); }
      }
    };

    ws.onmessage = (ev) => {
      let msg: RealtimeMessage;
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch {
        return;
      }
      const set = this.subs.get(msg.type);
      if (!set) return;
      for (const h of set) {
        try { h(msg.payload); } catch (err) { console.warn('[realtime] subscriber threw', err); }
      }
    };

    ws.onerror = (ev) => {
      // The browser doesn't give us a useful error, but the subsequent
      // onclose will trigger reconnection. Don't log noisily.
      void ev;
    };

    ws.onclose = () => {
      this.socket = null;
      const wasOpen = this.state === 'open';
      if (this.state === 'closed' || this.explicitlyClosed) {
        if (wasOpen) this.notifyConnected();
        return;
      }
      this.state = 'idle';
      if (wasOpen) this.notifyConnected();
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.explicitlyClosed) return;
    if (this.reconnectTimer) return;
    if (this.subs.size === 0) {
      // No subscribers; don't burn CPU reconnecting until someone wants frames.
      return;
    }
    this.reconnectAttempts += 1;
    const base = Math.min(30_000, 500 * 2 ** Math.min(this.reconnectAttempts - 1, 6));
    const jitter = Math.random() * 500;
    const delay = base + jitter;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  for (const raw of document.cookie.split(';')) {
    const c = raw.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

export const realtime = new RealtimeClient();
